<template>
  <modal
    name="modal--edit-notifications"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="350"
    :height="'auto'"
    :adaptive="true"
    :reset="true"
    :scrollable="true"
    draggable=".drag-handler"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    class="py-10"
  >
    <div
      class="flex flex-col text-black h-full bg-white overflow-hidden w-full rounded-sm justify-between p-4"
    >
      <div
        class="text-center text-4xl mb-3 font-extralight tracking-tight drag-handler leading-tight"
      >
        {{ `${label} Notifications` }}
      </div>

      <form @submit.prevent="handleConfirm" autocomplete="off">
        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Recipients
          <div
            class="normal-case mt-2 px-2 pb-2 border border-gray-300 rounded-lg"
          >
            <todo-list
              ref="emailList"
              :label="'Email'"
              placeholder="Add Email"
              :list="notifications.emailRecipients"
              :readonly="!isAdmin"
              @add="add('email', $event)"
              @remove="remove('email', $event)"
              class="text-base font-normal normal-case"
            />
            <todo-list
              ref="phoneList"
              :label="'Phone'"
              placeholder="Add 10-digit Number"
              validation-type="phone"
              :list="notifications.phoneRecipients"
              :readonly="!isAdmin"
              @add="add('phone', `+1${$event.replace(/-/g, '')}`)"
              @remove="remove('phone', $event)"
              class="text-base font-normal normal-case"
            />
          </div>
        </label>

        <div class="mt-6 flex flex-row justify-between text-lg text-gray-900">
          <span>Are these notifications active? </span>
          <s-switch tabindex="1" v-model="notifications.active" :disabled="!isAdmin" />
        </div>

        <transition-expand>
          <label
            v-if="type === 'priceChange' && notifications.active"
            class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          >
            Send if product price changes by
            <!-- </label> -->
            <div
              class="mt-2 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 pr-10 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            >
              <input
                class="appearance-none bg-transparent text-center leading-tight w-full"
                type="tel"
                autocomplete="off"
                v-model="notifications.percent"
                :disabled="!isAdmin"
              />
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 font-bold"
              >
                %
              </div>
            </div>
          </label>
        </transition-expand>

        <transition-expand>
          <div v-if="type === 'ordering' && notifications.active">
            <label
              class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
              for="notificationHour"
            >
              Notify If Not Ordered By
            </label>
            <div class="mt-2">
              <select
                class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
                id="notificationHour"
                v-model="notifications.hour"
                :disabled="!isAdmin"
                @click.stop
              >
                <option value="" disabled selected>select something...</option>
                <option
                  v-for="i in hourNumbers"
                  :key="`${i}-returned`"
                  :value="i"
                  v-text="formatHour(i)"
                />
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
              >
                <svg
                  class="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </transition-expand>

        <div class="flex flex-row justify-between mt-6">
          <button
            class="buttonOutline focus:shadow-outline focus:outline-none"
            type="button"
            @click="handleCancel"
          >
            Cancel
          </button>

          <button
            v-if="isAdmin"
            class="ml-4 button focus:shadow-outline focus:outline-none"
            type="submit"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { weekdays, hourNumbers } from '@/const.js'
import TodoList from '@/components/TodoList'
import SSwitch from '@/components/SSwitch'
import TransitionExpand from '@/components/TransitionExpand'
import { formatHour } from '@/utils.js'

export default {
  name: 'ModalEditNotifications',
  data() {
    return {
      weekdays,
      hourNumbers,
      type: null,
      notifications: {
        active: true,
        percent: null,
        emailRecipients: [],
        phoneRecipients: []
      },
      moneyConfig: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false /* doesn't work with directive */
      }
    }
  },
  components: {
    TodoList,
    SSwitch,
    TransitionExpand
  },
  computed: {
    ...mapGetters('storeModule', { storeNotifications: 'notifications' }),
    ...mapGetters('user', ['isAdmin']),
    label() {
      if (this.type === 'priceChange') return 'Price Change'
      else if (this.type === 'ordering') return 'Ordering'
      else return 'Task'
    }
  },
  methods: {
    formatHour,
    ...mapActions('config', ['setFabVisible', 'setDragLock']),
    ...mapActions('storeModule', ['updateNotificationType']),
    hideModal() {
      this.$modal.hide('modal--edit-notifications')
    },
    add(type, value) {
      this.notifications[`${type}Recipients`].push(value)
    },
    remove(type, value) {
      let index = this.notifications[`${type}Recipients`].indexOf(value)
      if (index > -1) this.notifications[`${type}Recipients`].splice(index, 1)
    },
    beforeOpen: function(event) {
      // If an ID for a product was passed, get that product info, if not, set blank
      if (event && event.params && event.params.id) {
        // create a local copy of the notification, not a reference, for temporary editing
        this.type = event.params.id
        this.notifications = {
          ...this.storeNotifications[event.params.id]
        }
        if (this.type === 'priceChange' && this.notifications.percent)
          this.notifications.percent = Number(this.notifications.percent)
      }
      this.setFabVisible(false)
      this.setDragLock(true)
    },
    beforeClose: function() {
      this.type = null
      this.notifications = {
        active: true,
        percent: null,
        emailRecipients: [],
        phoneRecipients: []
      }
      this.setFabVisible(true)
      this.setDragLock(false)
    },
    handleCancel: function() {
      this.hideModal()
    },
    handleConfirm: function() {
      if (this.type === 'priceChange' && this.notifications.percent)
        this.notifications.percent = Number(this.notifications.percent)
      this.updateNotificationType({
        type: this.type,
        data: { ...this.notifications }
      })
      this.hideModal()
    }
  }
}
</script>
